import React from 'react'
import {
  Slide,
  Box,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Typography,
  Alert,
  Button,
} from '@mui/material'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { detectCityFromURL } from '../../utils/cityDetector'
import { buildCantonUrl, normalizePathname } from '../../utils/cantonUrl'
import { useCities } from '../../context/CitiesContext'
import { getPropertiesByID, getTreePhotos, resolveTreeRoute } from '../../services'
import { getTreePhotoUrl } from '../../utils/imageUrl'
import {
  getTreeTenantRef,
  isDetailNotAllowedError,
  isFullTreeDetail,
  isInvalidSessionError,
} from '../../utils/treePermissions'
import { useTreePermissions } from '../../hooks/useTreePermissions'
import TreeRegistrationWizardV2 from '../../components/TreeRegistration'

const EDIT_NOT_ALLOWED_MESSAGE = 'No tienes permiso para editar este árbol.'
const SESSION_EXPIRED_MESSAGE = 'Tu sesión expiró. Vuelve a iniciar sesión.'
const LOAD_FAILED_MESSAGE = 'Error al cargar los datos del árbol.'

const getLoadErrorMessage = requestError => {
  if (isDetailNotAllowedError(requestError)) return EDIT_NOT_ALLOWED_MESSAGE
  if (isInvalidSessionError(requestError)) return SESSION_EXPIRED_MESSAGE
  return LOAD_FAILED_MESSAGE
}

const EditTreeWizardView = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { treeId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const currentCity = detectCityFromURL(location.pathname) || 'loja'
  const { getEnabledCantones } = useCities()
  const cantones = getEnabledCantones()
  const { canUpdateTree } = useTreePermissions()

  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState(null)
  const [treeData, setTreeData] = React.useState(null)
  const [existingPhotos, setExistingPhotos] = React.useState([])
  const [treeInactive, setTreeInactive] = React.useState(false)

  React.useEffect(() => {
    if (!treeId) {
      setError('ID de árbol no encontrado.')
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)
    setError(null)

    Promise.all([getPropertiesByID(treeId), getTreePhotos(treeId)])
      .then(([properties, rawPhotos]) => {
        if (cancelled) return
        if (!properties) {
          setError('No encontramos ningún árbol con este identificador. Puede que haya sido eliminado o que el enlace sea incorrecto.')
          return
        }
        // Solo se edita la ficha completa: una proyección parcial nunca llega al formulario
        if (!isFullTreeDetail(properties)) {
          setError(EDIT_NOT_ALLOWED_MESSAGE)
          return
        }
        const normalized = (rawPhotos || []).map(p => ({
          id: p.id_tp,
          filename: p.filename,
          originalName: p.originalName,
          size: p.size,
          preview: p.variants?.md || p.url || getTreePhotoUrl(treeId, p.filename),
          isExisting: true,
        }))
        setTreeData(properties)
        setExistingPhotos(normalized)
      })
      .catch(requestError => {
        if (cancelled) return
        setError(getLoadErrorMessage(requestError))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [treeId])

  React.useEffect(() => {
    if (!treeId) return
    let cancelled = false

    resolveTreeRoute(treeId)
      .then(result => {
        if (cancelled) return

        // resolveTreeRoute solo resuelve árboles activos (habilitado=true y no expirados).
        // Si el árbol existe (lo confirma /feature) pero el resolve responde found=false
        // sin error de servidor, está deshabilitado o eliminado lógicamente.
        setTreeInactive(result?.found === false && !result?.serverError)

        if (!result?.found || !result.paths?.edit) return

        const currentPath = normalizePathname(location.pathname)
        const canonicalPath = normalizePathname(result.paths.edit)
        if (canonicalPath !== currentPath) navigate(canonicalPath, { replace: true })
      })
      .catch(error => {
        if (!cancelled) console.error('[EditTreePage] Error resolviendo ruta:', error)
      })

    return () => {
      cancelled = true
    }
  }, [treeId, location.pathname, navigate])

  // Se calcula al pintar para no atar la carga a la sesión. Cubre al visitante, que recibe
  // la ficha completa pero no puede actualizar.
  const permissionError =
    treeData && !canUpdateTree(getTreeTenantRef(treeData)) ? EDIT_NOT_ALLOWED_MESSAGE : null
  const pageError = error || permissionError

  if (loading) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          minHeight: isMobile ? '60vh' : '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          px: 2,
          backgroundColor: 'background.paper',
        }}>
        <CircularProgress size={48} />
        <Typography variant="body2" color="text.secondary">
          Cargando datos del árbol...
        </Typography>
      </Box>
    )
  }

  if (pageError) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          minHeight: isMobile ? '60vh' : '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          px: 2,
          backgroundColor: 'background.paper',
        }}>
        <Alert severity="error" sx={{ width: '100%', maxWidth: 400 }}>
          {pageError}
        </Alert>
        <Button variant="outlined" onClick={() => navigate(buildCantonUrl(currentCity, cantones))}>
          Volver al mapa
        </Button>
      </Box>
    )
  }

  return (
    <Slide
      direction={isMobile ? 'up' : 'left'}
      in
      mountOnEnter
      unmountOnExit
      timeout={400}>
      <Box
        sx={{
          width: '100%',
          height: '100%',
          overflowY: 'auto',
          px: 1,
          backgroundColor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
          minHeight: isMobile ? '60vh' : '100vh',
        }}>
        <TreeRegistrationWizardV2
          mode="edit"
          treeData={treeData}
          existingPhotos={existingPhotos}
          treeInactive={treeInactive}
        />
      </Box>
    </Slide>
  )
}

export default EditTreeWizardView
